import { useQueryClient } from '@tanstack/react-query';
import * as R from 'ramda';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import useFilter from '../../../components/Crud/useFilter';
import { DataQueryKey } from '../../../hooks/useDataQuery/dataQuery.types';
import useHandleApiError from '../../../hooks/useHandleApiError';
import { useProductColorsSelloutSummary } from '../../../hooks/useProductColorsSelloutSummary';
import { ProductColorsSelloutSummary } from '../../../hooks/useProductColorsSelloutSummary.types';
import LangEnum from '../../../lang/LangEnum';
import { TeceoApiResponse } from '../../../lib/basicRepository';
import { getTotalProductColorSelloutKey } from '../../../services/modules/product-color-sellout/product-color-sellout.keys';
import {
  getFindProductGridKey,
  getProductGridKey,
} from '../../../services/modules/product-grid/product-grid.keys';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import useOrderProductsReloadStore from '../../../stores/OrderProductsReload/orderProductsReload.store';
import useProductDetailsStore from '../../../stores/ProductDetails/productDetails.store';
import snackbarStore from '../../../stores/Snackbar.store';
import { WithId } from '../../../types/Base.types';
import { buildOrderProductUniqueIdentifier } from '../../../utils/getOrderProductUniqueIdentifier';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useLoadingStatusStore } from '../../context/loadingStatus/LoadingStatus.store';
import { LoadingStatus } from '../../context/loadingStatus/loadingStatus.store.types';
import { UsedInOfflineMode } from '../../offline/types/UsedInOfflineMode';
import { AvailabilityEnum } from '../../products/ProductTypes';
import { kitActionsRepository } from '../../products/repositories/kitActions/kitActions.repository';
import { useTranslation } from '../../translation/TranslationContext';
import { useOrderProductsRachelTransition } from '../effects/useOrderProductsRachelTransition';
import usePendingOrderProductKeys from '../hooks/usePendingOrderProductKeys';
import useRegroupOnPendencyChange from '../hooks/useRegroupOnPendencyChange';
import useRemoveProductGridQueries from '../products/hooks/useRemoveProductGridQueries';
import { BatchOrderProduct } from '../products/interfaces/order-products-repository.types';
import {
  AddOrderProductFormState,
  ListSortViewEnum,
  OnSelectNewColor,
  OnSelectNewDeliveryWindow,
  OnSuccessAddProductWithPacks,
  OrderHeadersGroupBy,
  OrderHeadersGroupByEnum,
  OrderProduct,
  OrderProductsViewActionsEnum,
  Section,
  SortView,
  UpdateItemFormat,
} from '../products/orderProducts.types';
import { getGroupByInfo } from '../products/orderProducts.utils';
import useRelatedMaterials from '../products/product/grid/components/orderProductGrid/hooks/useRelatedMaterials';
import { getProductsByKitId } from '../products/section/orderProductsSection.utils';
import { ordersRepository } from '../repositories/ordersRepository/orders.repository';
import { useOrderContext } from './Order.context';
import { useOrderCalculateContext } from './OrderCalculate.context';
import { useOrderConditionContext } from './OrderCondition.context';
import { validateOrderProductsQuery } from './order.utils';
import {
  activateBarcodeDialog,
  closeBarcodeDialogAfterRead,
  disableBarcodeDialog,
  errorAfterTryToAddProduct,
  expandAllSections,
  expandSection,
  fetchOrderProductsResult,
  insertNewAddProduct,
  orderProductsInitialState,
  pushOrderProductsState,
  reducer,
  regroupByPendencyState,
  removeAddProduct,
  removeProduct,
  removeProducts,
  removeRemoveProduct,
  retractAllSections,
  retractSection,
  startFetchOrderProducts,
  startUpdateOrderProduct,
  stopUpdateOrderProduct,
  successAfterInsertAddProduct,
  toggleParentGroup,
  updateColorResult,
  updateDeliveryWindowAndAvailability,
  upsertOrderProductsState,
} from './orderProducts.state';

export const OrderProductsContext = createContext<any>({
  state: orderProductsInitialState,
  dispatch: () => {
    return;
  },
});

interface OrderProductsProviderProps extends UsedInOfflineMode {
  children: React.ReactNode;
}

const { handleMessage } = snackbarStore;

export const OrderProductsProvider = ({ children, isOfflineMode }: OrderProductsProviderProps) => {
  const params = useParams<WithId>();
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();
  const { team } = useApplicationContext();
  const removeProductGridQueries = useRemoveProductGridQueries();
  const { calculateSummary } = useOrderCalculateContext();
  const { invalidateOrderConditions } = useOrderConditionContext();
  const { getRelatedProductColorIds, refetchProductColors, refetchProductColorsSharingMaterial } =
    useRelatedMaterials();
  const { addFilters, initialValues: filterInitialValues } = useFilter();
  const { reviewAction, changeOrderReviewAction } = useOrderContext();
  const { handleLoadingStatus, isLoadingStatus } = useLoadingStatusStore(
    useShallow((state) => ({
      handleLoadingStatus: state.handleLoadingStatus,
      isLoadingStatus: state.loadingStatus === LoadingStatus.LOADING,
    })),
  );
  const sellout = useProductColorsSelloutSummary({ orderId: params.id });
  const reloadOrderItemsQuantity = useOrderBookStore((state) => state.reloadOrderItemsQuantity);

  const [state, dispatch] = useReducer(reducer, orderProductsInitialState);
  const orderId = params.id;
  const { pendingOrderProductKeys, getLatestPendingOrderProductKeys } =
    usePendingOrderProductKeys(orderId);
  const latestOrderProductsRequestRef = useRef(0);
  const { applyWithRachelTransition } = useOrderProductsRachelTransition(state.products);

  const groupBy = useMemo(() => {
    if (!filterInitialValues.GROUP_BY) {
      return [OrderHeadersGroupByEnum.DEFAULT] as OrderHeadersGroupBy[];
    }
    if (typeof filterInitialValues.GROUP_BY === 'string') {
      return [filterInitialValues.GROUP_BY] as OrderHeadersGroupBy[];
    }
    return filterInitialValues.GROUP_BY as OrderHeadersGroupBy[];
  }, [filterInitialValues.GROUP_BY]);

  const groupByInfo = useMemo(
    () => getGroupByInfo(filterInitialValues.GROUP_BY_INFO as string),
    [filterInitialValues.GROUP_BY_INFO],
  );
  const sortBy = useMemo(
    () => (filterInitialValues.SORT_BY as SortView) || (ListSortViewEnum.LAST_ADDED as SortView),
    [filterInitialValues.SORT_BY],
  );

  const grouping = useMemo(
    () => ({ view: groupBy, viewInfo: groupByInfo, pendingOrderProductKeys }),
    [groupBy, groupByInfo, pendingOrderProductKeys],
  );

  useRegroupOnPendencyChange({
    pendingOrderProductKeys,
    isEnabled: groupBy[0] === OrderHeadersGroupByEnum.PENDENCY && state.hasFetchedProducts,
    hasProductUpdating: state.products.some(({ updatingAvailability }) => updatingAvailability),
    regroup: () => dispatch(regroupByPendencyState(grouping)),
  });

  /**
   * @deprecated use pushOrderProducts instead
   */
  const handleSuccessAddProduct = async (
    product: OrderProduct,
    values: AddOrderProductFormState,
  ) => {
    reloadOrderItemsQuantity(false);
    calculateSummary();
    dispatch(successAfterInsertAddProduct({ addOrderProductState: values, product, grouping }));
    removeProductGridQueries([product.productColorId]);
    if (product.availability === AvailabilityEnum.MATERIAL_PRE_ORDER) {
      refetchProductColorsSharingMaterial(orderId, [product.productColorId]);
    }
    await queryClient.refetchQueries(validateOrderProductsQuery(orderId, team?.id));
    invalidateOrderConditions();
    queryClient.refetchQueries({
      queryKey: [team?.id, ...getTotalProductColorSelloutKey({ orderId: orderId })],
    });
  };

  const upsertOrderProducts = (orderProducts: OrderProduct[]) => {
    reloadOrderItemsQuantity(false);
    removeProductGridQueries(orderProducts.map((orderProduct) => orderProduct.productColorId));
    dispatch(upsertOrderProductsState(orderProducts, grouping));
  };

  const pushOrderProducts = async (orderProducts: OrderProduct[]) => {
    reloadOrderItemsQuantity(false);
    calculateSummary();
    removeProductGridQueries(orderProducts.map((orderProduct) => orderProduct.productColorId));
    dispatch(pushOrderProductsState(orderProducts, grouping));

    const preOrderProductColorIds = orderProducts
      .filter((op) => op.availability === AvailabilityEnum.MATERIAL_PRE_ORDER)
      .map((op) => op.productColorId);

    if (preOrderProductColorIds.length) {
      refetchProductColorsSharingMaterial(orderId, preOrderProductColorIds);
    }
    await queryClient.refetchQueries(validateOrderProductsQuery(orderId, team?.id));
    invalidateOrderConditions();
  };

  const handleErrorAddProduct = (addProduct: AddOrderProductFormState, errorMessage?: string) => {
    dispatch(errorAfterTryToAddProduct(addProduct, errorMessage));
  };

  const onRemoveProducts = useCallback(
    async (products: BatchOrderProduct[]) => {
      const productUniqueIdentifiers: string[] = [];

      await queryClient.refetchQueries(validateOrderProductsQuery(orderId, team?.id));
      products.forEach((product) => {
        queryClient.removeQueries({
          queryKey: getProductGridKey({
            queryKey: DataQueryKey.PRODUCT_GRID,
            teamId: team?.id,
            params: {
              productColorId: product.productColorId,
              availability: product.availability,
              deliveryWindowId: product?.deliveryWindowId ?? '',
              orderId,
            },
          }),
        });
        productUniqueIdentifiers.push(
          buildOrderProductUniqueIdentifier({
            ...product,
            deliveryWindowId: product.deliveryWindowId ?? null,
          }),
        );
      });
      dispatch(removeProducts(productUniqueIdentifiers));
      reloadOrderItemsQuantity(false);
      calculateSummary();
      invalidateOrderConditions();
    },
    [
      orderId,
      calculateSummary,
      invalidateOrderConditions,
      queryClient,
      reloadOrderItemsQuantity,
      state.products,
      team,
    ],
  );

  const handleClickRemove = useCallback(
    async (product: OrderProduct, sectionIndex: number) => {
      dispatch(removeProduct(product, sectionIndex));

      await handleLoadingStatus({
        requestFn: () =>
          ordersRepository(orderId).deleteOrderProduct({
            availabilities: [product.availability],
            deliveryWindowId: product.deliveryWindow?.id,
            productColorId: product.productColorId,
            productId: product.productId,
          }),
        onSuccess: async () => {
          await queryClient.refetchQueries(validateOrderProductsQuery(orderId, team?.id));
          queryClient.removeQueries({
            queryKey: getProductGridKey({
              queryKey: DataQueryKey.PRODUCT_GRID,
              teamId: team?.id,
              params: {
                productColorId: product.productColorId,
                availability: product.availability,
                deliveryWindowId: product?.deliveryWindow?.id,
                orderId,
              },
            }),
          });
        },
        onFailure() {
          dispatch(removeRemoveProduct(product, sectionIndex));
        },
      });

      reloadOrderItemsQuantity(false);
      calculateSummary();
      invalidateOrderConditions();
    },
    [
      handleLoadingStatus,
      reloadOrderItemsQuantity,
      team,
      calculateSummary,
      invalidateOrderConditions,
      orderId,
      queryClient,
    ],
  );

  const handleRemoveKitOrderProducts = useCallback(
    async (kitId: string) => {
      const productsFromKit = state.products.reduce<BatchOrderProduct[]>((acc, product) => {
        if (product.kit?.id === kitId) {
          acc.push({
            ...product,
            deliveryWindowId: product.deliveryWindow?.id,
          });
        }

        return acc;
      }, []);

      await onRemoveProducts(productsFromKit);
    },
    [onRemoveProducts, state.products],
  );

  const handleRemoveKitFromOrder = useCallback(
    async (kitId: string) => {
      await handleLoadingStatus({
        requestFn: async () =>
          await kitActionsRepository(isOfflineMode).unshopKit({ kitId, orderId: orderId }),
        onFailure: (error) => {
          const errorMessage = getApiErrorMessage(
            error,
            translate(LangEnum.REMOVE_ORDER_ITEMS_DEFAULT_ERROR_MESSAGE),
          );

          handleMessage({ type: 'error', message: errorMessage });
        },
        onSuccess: () => handleRemoveKitOrderProducts(kitId),
      });
    },
    [
      orderId,
      isOfflineMode,
      translate,
      getApiErrorMessage,
      handleLoadingStatus,
      handleRemoveKitOrderProducts,
    ],
  );

  const handleSubmitAddProduct = (addProduct: AddOrderProductFormState) => {
    dispatch(insertNewAddProduct([addProduct]));
  };

  const handleClickRemoveAddProduct = (product: AddOrderProductFormState) => {
    dispatch(removeAddProduct(product));
  };

  const onSelectNewDeliveryWindow: OnSelectNewDeliveryWindow = useCallback(
    async ({
      grid,
      productIndex,
      sectionIndex,
      dwAvailability,
      newAvailability,
      lastAvailability,
    }) => {
      if (state.products[productIndex]?.updatingAvailability) {
        return;
      }

      const product = !R.isNil(sectionIndex) && state.products[productIndex];

      const deliveryWindowId = dwAvailability?.deliveryWindow?.id || null;

      const formatToUpdate: UpdateItemFormat[] = grid
        .filter((item) => !!item.orderItemId && item.skuId)
        .map((item) => ({
          quantity: item.orderItemQuantity || 0,
          skuId: item.skuId as string,
          id: item.orderItemId,
          deliveryWindowId,
          availability: newAvailability,
        }));

      dispatch(startUpdateOrderProduct(productIndex, sectionIndex));
      const response = await handleLoadingStatus<TeceoApiResponse<OrderProduct>, { label: string }>(
        {
          requestFn: () =>
            ordersRepository(orderId).changeOrderProductDeliveryWindow(formatToUpdate),
          onSuccess: async () => {
            const mustRefetchRelatedProductColors = [
              dwAvailability?.availability,
              newAvailability,
              lastAvailability,
            ].some((availability) => availability === AvailabilityEnum.MATERIAL_PRE_ORDER);

            if (mustRefetchRelatedProductColors && product) {
              const productColorIds = [product.productColorId];
              refetchProductColorsSharingMaterial(orderId, productColorIds);
            }
            await queryClient.refetchQueries(validateOrderProductsQuery(orderId, team?.id));
          },
          onFailure: (err) => {
            const translation = translate(err.code as LangEnum, err.data, err.message);
            handleMessage({
              type: 'error',
              message:
                translation ||
                translate(LangEnum.ORDER_PRODUCTS_DELIVERY_WINDOW_CHANGE_ERROR_MESSAGE),
            });
            dispatch(stopUpdateOrderProduct(productIndex, sectionIndex));
          },
        },
      );

      if (response.ok && response.data) {
        calculateSummary();
        dispatch(
          updateDeliveryWindowAndAvailability({
            product: response.data,
            productIndex,
            sectionIndex,
          }),
        );
        invalidateOrderConditions();

        if (product) {
          queryClient.removeQueries({
            queryKey: [team?.id, DataQueryKey.PRODUCT_GRID, product.productColorId],
          });
          queryClient.removeQueries({
            queryKey: [
              team?.id,
              DataQueryKey.PRODUCT_DETAILS_EMPTY_AVAILABILITIES,
              product.productId,
            ],
          });
        }
      }
    },
    [
      orderId,
      team?.id,
      translate,
      queryClient,
      state.products,
      calculateSummary,
      handleLoadingStatus,
      invalidateOrderConditions,
      refetchProductColorsSharingMaterial,
    ],
  );

  const onSelectNewColor: OnSelectNewColor = useCallback(
    async ({ grid, productColor, productIndex, sectionIndex }) => {
      const product = !R.isNil(sectionIndex) && state.products[productIndex];

      if (!product) {
        handleMessage({
          type: 'error',
          message: translate(LangEnum.ORDER_PRODUCTS_NOT_FOUND_MESSAGE),
        });
        return;
      }

      if (product.updatingAvailability) {
        return;
      }

      dispatch(startUpdateOrderProduct(productIndex, sectionIndex));

      const mustRefetchRelatedProductColors =
        product.availability === AvailabilityEnum.MATERIAL_PRE_ORDER;

      const oldProductColorId = product.productColorId;
      const newProductColorId = productColor.id;

      const relatedProductColorsBeforeChange = mustRefetchRelatedProductColors
        ? await getRelatedProductColorIds(orderId, [oldProductColorId])
        : [];

      const response = await handleLoadingStatus({
        requestFn: () =>
          ordersRepository(orderId).changeOrderProductColor({
            orderItemIds: grid
              .filter((item) => !!item.orderItemId)
              .map(({ orderItemId }) => orderItemId) as string[],
            productId: product.productId,
            newProductColorId: productColor.id,
            availability: product.availability,
            deliveryWindowId: product.deliveryWindow?.id,
          }),
        onSuccess: async () => {
          if (mustRefetchRelatedProductColors) {
            const relatedProductColorsAfterChange = await getRelatedProductColorIds(orderId, [
              newProductColorId,
            ]);
            const productColorsToRefetch = R.uniqBy(
              (id) => id,
              [...relatedProductColorsBeforeChange, ...relatedProductColorsAfterChange],
            );
            refetchProductColors(orderId, productColorsToRefetch);
          }

          await queryClient.refetchQueries(validateOrderProductsQuery(orderId, team?.id));
          await sellout.refetch();
        },
        onFailure: (err) => {
          dispatch(stopUpdateOrderProduct(productIndex, sectionIndex));

          const translation = translate(err.code as LangEnum, err.data, err.message);
          handleMessage({
            type: 'error',
            message:
              translation ||
              translate(LangEnum.ORDER_PRODUCTS_DELIVERY_WINDOW_CHANGE_ERROR_MESSAGE),
          });
        },
      });

      if (response.data && response.ok) {
        dispatch(updateColorResult({ product: response.data, productIndex, sectionIndex }));
        invalidateOrderConditions();
        await queryClient.refetchQueries({
          type: 'active',
          queryKey: [team?.id, DataQueryKey.ORDER_CALCULATE_SUMMARY],
        });
        queryClient.removeQueries({
          queryKey: [
            team?.id,
            ...getFindProductGridKey({
              availability: product.availability,
              productColorId: product.productColorId,
              deliveryWindowId: product.deliveryWindow?.id,
              orderId,
              isOfflineMode,
            }),
          ],
        });
        removeProductGridQueries([newProductColorId]);
      }
    },
    [
      orderId,
      isOfflineMode,
      getRelatedProductColorIds,
      removeProductGridQueries,
      handleLoadingStatus,
      invalidateOrderConditions,
      queryClient,
      refetchProductColors,
      state.products,
      team?.id,
      translate,
      isOfflineMode,
    ],
  );

  const changeView = useCallback(() => {
    switch (reviewAction) {
      case OrderProductsViewActionsEnum.EXPAND_ALL:
        dispatch(expandAllSections());
        break;
      case OrderProductsViewActionsEnum.HIDE_ALL:
        dispatch(retractAllSections());
        break;
      default:
        return;
    }

    changeOrderReviewAction(OrderProductsViewActionsEnum.NONE);
  }, [reviewAction, changeOrderReviewAction]);

  const updateGroupProductsCount = (sectionIndex: number) => {
    addFilters({ VIEW_ACTION: OrderProductsViewActionsEnum.NONE });
    if (state.sections[sectionIndex].isExpanded) {
      dispatch(retractSection({ sectionIndex }));
      return;
    }
    dispatch(expandSection({ sectionIndex }));
  };

  const updateParentGroupExpansion = (parentGroupKey: string, isExpanded: boolean) => {
    addFilters({ VIEW_ACTION: OrderProductsViewActionsEnum.NONE });
    dispatch(toggleParentGroup({ parentGroupKey, isExpanded }));
  };

  useEffect(() => {
    changeView();
  }, [changeView]);

  const { productDetailsModalState } = useProductDetailsStore(
    useShallow((productDetailsState) => ({
      productDetailsModalState: productDetailsState.productDetailsModalState,
    })),
  );

  const getOrderProducts = async (isMounted: boolean, shouldApplyRachelTransition = false) => {
    const requestId = ++latestOrderProductsRequestRef.current;

    if (R.isEmpty(state.products)) {
      dispatch(startFetchOrderProducts());
    }

    const response = await ordersRepository(orderId, isOfflineMode).getOrderProductsV2({ sortBy });
    if (!isMounted) {
      return;
    }

    const applyResponse = () => {
      if (requestId !== latestOrderProductsRequestRef.current) {
        return false;
      }

      dispatch(
        fetchOrderProductsResult({
          response,
          view: groupBy,
          viewInfo: groupByInfo,
          pendingOrderProductKeys: getLatestPendingOrderProductKeys(),
        }),
      );

      changeOrderReviewAction(OrderProductsViewActionsEnum.EXPAND_ALL);
      return true;
    };

    if (shouldApplyRachelTransition && response.ok && response.data) {
      await applyWithRachelTransition({ nextItems: response.data, apply: applyResponse });
      return;
    }

    applyResponse();
  };

  useEffect(() => {
    let isMounted = true;

    if (!productDetailsModalState.isOpen) {
      getOrderProducts(isMounted);
      calculateSummary();
    }

    return () => {
      isMounted = false;
    };
  }, [
    JSON.stringify(groupBy),
    JSON.stringify(groupByInfo),
    sortBy,
    productDetailsModalState.isOpen,
  ]);

  useEffect(
    () => () => {
      latestOrderProductsRequestRef.current += 1;
    },
    [],
  );

  const orderReloadSignal = useOrderProductsReloadStore((state) => state.reloadSignals[orderId]);
  const previousReloadSignalRef = useRef(orderReloadSignal);

  useEffect(() => {
    if (!orderReloadSignal || orderReloadSignal === previousReloadSignalRef.current) {
      return;
    }

    previousReloadSignalRef.current = orderReloadSignal;
    getOrderProducts(true, orderReloadSignal.isFromRachel);
    calculateSummary();
  }, [orderReloadSignal]);

  const onSuccessAddProductWithPacks: OnSuccessAddProductWithPacks = async (
    availability,
    productColorId,
  ) => {
    reloadOrderItemsQuantity(false);
    calculateSummary();
    getOrderProducts(true);
    if (availability === AvailabilityEnum.MATERIAL_PRE_ORDER && productColorId) {
      refetchProductColorsSharingMaterial(orderId, [productColorId]);
    }
    await queryClient.refetchQueries(validateOrderProductsQuery(orderId, team?.id));
    invalidateOrderConditions();
  };

  const value: OrderProductsContextProps = {
    isLoading: state.isLoading,
    isOfflineMode,
    hasFetchedProducts: state.hasFetchedProducts,
    isLoadingRequest: isLoadingStatus,
    groupBy,
    productColorIds: state.products.map((p) => p.productColorId),
    sections: state.sections,
    collapsedParentGroupKeys: state.collapsedParentGroupKeys,
    products: state.products,
    productsByKitId: getProductsByKitId(state.products),
    openBarcodeDialog: state.openBarcodeDialog,
    activeBarcodeDialog: state.activeBarcodeDialog,
    sellout,
    onRemoveProducts,
    onSelectNewColor,
    onClickRemoveAddProduct: handleClickRemoveAddProduct,
    onSubmitAddProduct: handleSubmitAddProduct,
    onSuccessAddProduct: handleSuccessAddProduct,
    onErrorAddProduct: handleErrorAddProduct,
    onClickRemove: handleClickRemove,
    onClickRemoveKitFromOrder: handleRemoveKitFromOrder,
    onSelectNewDeliveryWindow,
    updateGroupProductsCount,
    updateParentGroupExpansion,
    onCloseBarcodeDialogAfterRead: () => dispatch(closeBarcodeDialogAfterRead()),
    activateBarcode: () => dispatch(activateBarcodeDialog()),
    disableBarcode: () => dispatch(disableBarcodeDialog()),
    onSuccessAddProductWithPacks,
    pushOrderProducts,
    upsertOrderProducts,
  };

  return <OrderProductsContext.Provider value={value}>{children}</OrderProductsContext.Provider>;
};

interface OrderProductsContextProps extends UsedInOfflineMode {
  isLoading: boolean;
  hasFetchedProducts: boolean;
  products: OrderProduct[];
  productColorIds: string[];
  productsByKitId: Map<string, OrderProduct[]>;
  groupBy: OrderHeadersGroupBy[];
  sections: Array<Section>;
  collapsedParentGroupKeys: string[];
  openBarcodeDialog: boolean;
  activeBarcodeDialog: boolean;
  isLoadingRequest: boolean;
  sellout: ProductColorsSelloutSummary;
  onRemoveProducts: (products: BatchOrderProduct[]) => Promise<void>;
  onSelectNewColor: OnSelectNewColor;
  onClickRemoveAddProduct: (product: AddOrderProductFormState) => void;
  /**
   * @deprecated use pushOrderProducts instead
   */
  onSuccessAddProduct: (
    product: OrderProduct,
    addOrderProductState: AddOrderProductFormState,
  ) => void;
  pushOrderProducts: (orderProducts: OrderProduct[]) => void;
  upsertOrderProducts: (orderProducts: OrderProduct[]) => void;
  onErrorAddProduct: (formValues: AddOrderProductFormState) => void;
  onSubmitAddProduct: (product: AddOrderProductFormState) => void;
  onClickRemove: (product: OrderProduct, sectionIndex: number) => Promise<void>;
  onSelectNewDeliveryWindow: OnSelectNewDeliveryWindow;
  updateGroupProductsCount: (sectionIndex: number) => void;
  updateParentGroupExpansion: (parentGroupKey: string, isExpanded: boolean) => void;
  onCloseBarcodeDialogAfterRead: () => void;
  activateBarcode: () => void;
  disableBarcode: () => void;
  onSuccessAddProductWithPacks: OnSuccessAddProductWithPacks;
  onClickRemoveKitFromOrder: (kitId: string) => Promise<void>;
}

export const useOrderProductsContext = () =>
  useContext<OrderProductsContextProps>(OrderProductsContext);
